/** @file dropInput: a PNG dragged over the viewport lights the part under it; dropped, it fills that part. */
import { fillPartImage } from './fillPartImage';
import { quadAt } from './quadAt';
import { targetPoint } from './targetPoint';
import type { ViewportCtx } from './viewportCtx';

/** True when the drag carries files (the only drags the viewport takes). */
const hasFiles = (e: DragEvent): boolean => e.dataTransfer?.types.includes('Files') ?? false;

/** The first PNG of a drop (by type, else by name). */
function pngOf(e: DragEvent): File | null {
  const files = Array.from(e.dataTransfer?.files ?? []);
  return files.find((f) => f.type === 'image/png' || f.name.toLowerCase().endsWith('.png')) ?? null;
}

/**
 * Edit mode only. `dragover` with files: the quad under the pointer becomes `ctx.quad.drop` (drawn brightest, quads
 * view on or off); `dragleave` clears it; `drop` fills that part's shown tile with the first PNG (`fillPartImage`,
 * "Fill <slot> from drop"), or toasts when it fell on no part or held no PNG. Returns the removal.
 */
export function dropInput(ctx: ViewportCtx): () => void {
  const over = (e: DragEvent): void => {
    if (ctx.mode !== 'edit' || !hasFiles(e)) return;
    e.preventDefault();
    if (e.dataTransfer !== null) e.dataTransfer.dropEffect = 'copy';
    ctx.quad.drop = quadAt(ctx, targetPoint(ctx, e));
  };
  const leave = (): void => { ctx.quad.drop = -1; };
  const drop = (e: DragEvent): void => {
    if (ctx.mode !== 'edit' || !hasFiles(e)) return;
    e.preventDefault();
    const target = quadAt(ctx, targetPoint(ctx, e));
    ctx.quad.drop = -1;
    const file = pngOf(e);
    const s = ctx.store.getState();
    if (file === null) { s.notify('warning', 'Drop a PNG image onto a part'); return; }
    if (target < 0) { s.notify('warning', 'Drop the image onto a part of the puppet'); return; }
    void file.arrayBuffer().then((b) => fillPartImage(ctx, target, new Uint8Array(b), 'drop'));
  };
  const listeners: [string, (e: DragEvent) => void][] = [['dragover', over], ['dragleave', leave], ['drop', drop]];
  for (const [type, fn] of listeners) ctx.container.addEventListener(type, fn as EventListener);
  return () => { for (const [type, fn] of listeners) ctx.container.removeEventListener(type, fn as EventListener); };
}
